/**
 * spoken-address — turn a stored US address into the words a person would say.
 *
 * "18095 Hwy 18 Ste 2, Apple Valley, CA 92307" is what a chart or a practice's
 * location row holds. Read aloud as written, the voice says "hwy", "ste" and
 * "ca", or guesses — and a framework line is spoken EXACTLY as written
 * (`say.ts`), so nothing downstream gets a chance to fix it. This is the same
 * job `spokenDigits` does for a phone number: infra owns pronunciation of the
 * values it hands over, because the model cannot be trusted to and a verbatim
 * line gives it no chance to.
 *
 * SPEECH ONLY. The stored value, the SMS (`office-lines.ts`), the export and the
 * UI keep the short form — a text message reading "Suite" where the chart says
 * "Ste" is a different string for no gain.
 *
 * A FIXED LEXICAL TABLE, NOT A CLASSIFIER. This answers no intent question; it
 * is the USPS abbreviation list read in reverse, the way `spokenDigits` is the
 * digit list. English words whatever the call language: a street name is a
 * proper noun, and "Calle" for "St" would name a street that does not exist.
 *
 * POSITION DECIDES, because the abbreviations collide. "St" is Street at the end
 * of a name and Saint at its start ("St Andrews Dr"); "E" is East before a name
 * and the name itself in "12 E St"; "Dr" is Doctor in "Dr Martin Luther King Jr
 * Blvd". Anything the rules do not recognise passes through unchanged rather
 * than being guessed at, full words are left alone, and the result is
 * idempotent — running it on its own output changes nothing.
 */

/** Street suffixes, abbreviation → spoken word. Full words map to themselves. */
const SUFFIXES: Record<string, string> = {
  aly: 'Alley',
  alley: 'Alley',
  ave: 'Avenue',
  av: 'Avenue',
  avenue: 'Avenue',
  blvd: 'Boulevard',
  boulevard: 'Boulevard',
  byp: 'Bypass',
  ctr: 'Center',
  cntr: 'Center',
  center: 'Center',
  cir: 'Circle',
  circle: 'Circle',
  ct: 'Court',
  court: 'Court',
  cv: 'Cove',
  cove: 'Cove',
  cres: 'Crescent',
  xing: 'Crossing',
  dr: 'Drive',
  drive: 'Drive',
  expy: 'Expressway',
  expressway: 'Expressway',
  ext: 'Extension',
  extension: 'Extension',
  fwy: 'Freeway',
  freeway: 'Freeway',
  hts: 'Heights',
  hwy: 'Highway',
  highway: 'Highway',
  holw: 'Hollow',
  jct: 'Junction',
  ln: 'Lane',
  lane: 'Lane',
  lp: 'Loop',
  loop: 'Loop',
  mnr: 'Manor',
  pkwy: 'Parkway',
  pky: 'Parkway',
  parkway: 'Parkway',
  pl: 'Place',
  place: 'Place',
  plz: 'Plaza',
  plaza: 'Plaza',
  pt: 'Point',
  rd: 'Road',
  road: 'Road',
  rdg: 'Ridge',
  rte: 'Route',
  route: 'Route',
  sq: 'Square',
  square: 'Square',
  st: 'Street',
  str: 'Street',
  street: 'Street',
  ter: 'Terrace',
  terrace: 'Terrace',
  tpke: 'Turnpike',
  trl: 'Trail',
  trail: 'Trail',
  way: 'Way',
};

/** Directionals. Full words map to themselves so a mixed line still parses. */
const DIRECTIONS: Record<string, string> = {
  n: 'North',
  s: 'South',
  e: 'East',
  w: 'West',
  ne: 'Northeast',
  nw: 'Northwest',
  se: 'Southeast',
  sw: 'Southwest',
  north: 'North',
  south: 'South',
  east: 'East',
  west: 'West',
  northeast: 'Northeast',
  northwest: 'Northwest',
  southeast: 'Southeast',
  southwest: 'Southwest',
};

/**
 * Secondary-unit designators. They mark where the street name ENDS, which is
 * why the full words are listed too: "Main St Suite 200" has to find its suffix
 * before "Suite", or "St" reads as a name-start "Saint".
 */
const UNITS: Record<string, string> = {
  apt: 'Apartment',
  apartment: 'Apartment',
  bldg: 'Building',
  building: 'Building',
  dept: 'Department',
  department: 'Department',
  fl: 'Floor',
  flr: 'Floor',
  floor: 'Floor',
  rm: 'Room',
  room: 'Room',
  spc: 'Space',
  space: 'Space',
  ste: 'Suite',
  suite: 'Suite',
  unit: 'Unit',
  lot: 'Lot',
};

/** Words that open a name: "St Andrews", "Mt Vernon", "Dr Martin Luther King". */
const NAME_PREFIXES: Record<string, string> = {
  st: 'Saint',
  mt: 'Mount',
  ft: 'Fort',
  dr: 'Doctor',
};

/** Words that are only ever one thing, wherever they sit. */
const ANYWHERE: Record<string, string> = {
  jr: 'Junior',
};

/** Route designators that read as words only when a number follows. */
const ROUTES: Record<string, string> = {
  hwy: 'Highway',
  rte: 'Route',
  rt: 'Route',
  sr: 'State Route',
  cr: 'County Road',
  us: 'U.S.',
};

const STATES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  PR: 'Puerto Rico',
  GU: 'Guam',
  VI: 'U.S. Virgin Islands',
  AS: 'American Samoa',
  MP: 'Northern Mariana Islands',
};

const HOUSE_NUMBER = /^\d+[A-Za-z]?(?:-\d+[A-Za-z]?)?$|^\d+\/\d+$/;
const ROUTE_NUMBER = /^\d+[A-Za-z]?$/;
const INTERSTATE = /^I-(\d+)$/i;
const ZIP = /^(\d{5})(?:-\d{4})?$/;
const STATE_ZIP_TAIL = /^(.*?)\s*\b([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;

/** A token's lookup key: lower-cased, trailing periods dropped ("St." → "st"). */
function keyOf(token: string): string {
  return token.replace(/\.+$/, '').toLowerCase();
}

/**
 * The full state name for a two-letter code ("CA" → "California"). Anything
 * that is not a known code — already spelled out, a province, a typo — comes
 * back trimmed and unchanged.
 */
export function spokenState(code: string): string {
  const trimmed = code.trim();
  return STATES[trimmed.toUpperCase()] ?? trimmed;
}

/**
 * A ZIP code spelled digit by digit ("92307" → "9 2 3 0 7"), the way a
 * receptionist reads one. A ZIP+4 is read as its five digits — the extension is
 * routing nobody writes down from a phone call. Anything that is not a ZIP comes
 * back trimmed and unchanged.
 */
export function spokenZip(zip: string): string {
  const trimmed = zip.trim();
  const match = ZIP.exec(trimmed);
  return match?.[1] ? match[1].split('').join(' ') : trimmed;
}

/** Expand the unit part of a street ("Ste 2", "#12", "Bldg B Rm 4"). */
function expandUnitTokens(tokens: string[]): string[] {
  return tokens.map((token) => {
    if (token === '#') return 'number';
    if (token.startsWith('#')) return `number ${token.slice(1)}`;
    return UNITS[keyOf(token)] ?? token;
  });
}

/** Expand one street line with no commas in it ("500 N Main St Ste 200"). */
function expandStreetTokens(tokens: string[]): string[] {
  const keys = tokens.map(keyOf);

  // P O BOX is a box, not a street: nothing after it is a name.
  if ((keys[0] === 'po' || keys[0] === 'p.o') && keys[1] === 'box') {
    return ['P O', ...tokens.slice(1)];
  }

  // Where the street name ends and the unit begins. Never the first token: a
  // street can be called "Lot" or "Unit" and still have a number.
  const unitAt = tokens.findIndex((t, i) => i > 0 && (t.startsWith('#') || keys[i]! in UNITS));
  const end = unitAt === -1 ? tokens.length : unitAt;
  const out = tokens.slice(0, end);
  const unit = unitAt === -1 ? [] : expandUnitTokens(tokens.slice(unitAt));

  let start = out.length > 0 && HOUSE_NUMBER.test(out[0]!) ? 1 : 0;
  const length = end - start;

  // A PRE-DIRECTIONAL only when a name follows it that is not just the suffix:
  // "500 N Main St" is North Main, but "12 E St" is a street called E.
  const first = keys[start];
  if (first !== undefined && first in DIRECTIONS) {
    const names = keys.slice(start + 1, end).filter((k) => !(k in SUFFIXES || k in DIRECTIONS));
    if (names.length > 0) {
      out[start] = DIRECTIONS[first]!;
      start += 1;
    }
  }

  for (let i = start; i < end; i++) {
    const token = out[i]!;
    const key = keys[i]!;
    const next = keys[i + 1];
    const atNameStart = i === start;

    const interstate = INTERSTATE.exec(token);
    if (interstate) {
      out[i] = `Interstate ${interstate[1]}`;
      continue;
    }
    // "Hwy 18", "SR 2", "US 395", and "Co Rd 5" — a route is a word and a number.
    if (key in ROUTES && next !== undefined && (ROUTE_NUMBER.test(next) || next === 'hwy')) {
      out[i] = ROUTES[key]!;
      continue;
    }
    if (key === 'co' && next === 'rd') {
      out[i] = 'County';
      continue;
    }
    if (key in ANYWHERE) {
      out[i] = ANYWHERE[key]!;
      continue;
    }
    // "St Andrews", "Dr Martin Luther King" — a prefix only when a name follows.
    if (atNameStart && key in NAME_PREFIXES && i + 1 < end) {
      out[i] = NAME_PREFIXES[key]!;
      continue;
    }
    // A lone suffix is the name ("100 St" stays as written); one with company
    // reads as the word, at the start too — "Ave B" is Avenue B.
    if (key in SUFFIXES && length >= 2) {
      out[i] = SUFFIXES[key]!;
      continue;
    }
    // A POST-DIRECTIONAL follows a suffix that itself followed a name: "Main St
    // NW" is Northwest, "Avenue N" is a street called N.
    const prev = keys[i - 1];
    if (key in DIRECTIONS && prev !== undefined && prev in SUFFIXES && i - 1 > start) {
      out[i] = DIRECTIONS[key]!;
    }
  }

  return [...out, ...unit];
}

/**
 * A city name as spoken: only a leading "St", "Mt" or "Ft" changes ("St Louis"
 * → "Saint Louis"). A city is a proper noun, so nothing else in it is touched.
 */
export function spokenCity(city: string): string {
  const tokens = city.trim().replace(/\s+/g, ' ').split(' ');
  const key = keyOf(tokens[0] ?? '');
  if (tokens.length > 1 && key !== 'dr' && key in NAME_PREFIXES) {
    tokens[0] = NAME_PREFIXES[key]!;
  }
  return tokens.join(' ');
}

/** Split a segment's trailing "CA 92307" off, when the code is a real state. */
function splitStateZip(segment: string): { head: string; tail: string[] } {
  const match = STATE_ZIP_TAIL.exec(segment);
  if (match && match[2]!.toUpperCase() in STATES) {
    return { head: match[1]!.trim(), tail: [spokenState(match[2]!), spokenZip(match[3]!)] };
  }
  return { head: segment, tail: [] };
}

/**
 * The spoken form of a one-line address, whatever shape it arrives in — the
 * street on file ("19250 Pinto Way"), or the whole line a patient corrected it
 * to ("500 N Main St Apt 4, Apple Valley, CA 92307").
 *
 * The first comma-separated segment is the street. A later segment is a unit
 * ("Ste 2"), a state and ZIP, or a city — which only has a leading "St"/"Mt"/
 * "Ft" spelled out. A blank input comes back as the empty string.
 */
export function spokenAddress(line: string): string {
  const segments = line
    .split(',')
    .map((s) => s.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

  const spoken: string[] = [];
  segments.forEach((segment, index) => {
    const { head, tail } = splitStateZip(segment);
    if (head) {
      const tokens = head.split(' ');
      if (index === 0) {
        spoken.push(expandStreetTokens(tokens).join(' '));
      } else if (keyOf(tokens[0]!) in UNITS || tokens[0]!.startsWith('#')) {
        spoken.push(expandUnitTokens(tokens).join(' '));
      } else if (tokens.length === 1 && head.toUpperCase() in STATES) {
        spoken.push(spokenState(head));
      } else if (ZIP.test(head)) {
        spoken.push(spokenZip(head));
      } else {
        spoken.push(spokenCity(head));
      }
    }
    spoken.push(...tail);
  });
  return spoken.join(', ');
}

export interface UsAddressParts {
  street: string;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

/**
 * The spoken form of a structured address, for when the parts are known and
 * nothing has to be inferred — a practice location's row. "18095 Hwy 18,
 * Apple Valley, CA 92307" → "18095 Highway 18, Apple Valley, California,
 * 9 2 3 0 7". Missing parts are left out.
 */
export function spokenUsAddress(parts: UsAddressParts): string {
  return [
    spokenAddress(parts.street),
    parts.city ? spokenCity(parts.city) : '',
    parts.state ? spokenState(parts.state) : '',
    parts.zip ? spokenZip(parts.zip) : '',
  ]
    .filter(Boolean)
    .join(', ');
}
